import {AgendaelementId, DokumentId, SaksdokumentModell} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {concat, concatMap, from, Observable} from "rxjs";
import {ValgtFil} from "./komponenter/opplastingsstatus-skyfiler/opplastingsstatus-skyfiler";
import {Debugger} from "debug";

/**
 * Abstrakt basisklasse for opplasting av skyfiler til sak.
 */
export abstract class SkyfilOpplasting<SPESIFIKASJON, SKYFIL extends Skyfil> {

    constructor(private debug: Debugger) {
    }

    //offentlige metoder

    /**
     * Laster opp angitte filer under angitt sak.
     * Dersom det allerede eksisterer et dokument med samme filnavn blir bruker spurt om vedkommende vil registrere det som en ny versjon.
     * Filer vil bli lastet opp sekvensielt. Dette da serversiden ikke takler
     * parallelle kall til sak/opprettDokument (pga hibernate List oppførsel)
     *
     * Returnert observable signaliserer hver gang et dokument har blitt lastet opp.
     * Dersom opplasting feilet vil OpplastetDokument.feil være satt til error-objektet,
     * og opplasting vil fortsette med neste fil i køen.
     */
    lastOpp(filer: ValgteSkyfiler<SKYFIL>, sakId: AgendaelementId, eksisterendeDokument: EksisterendeDokumentFunksjon): Observable<OpplastetDokument<SKYFIL>> {
        this.debug('lastOpp', filer, sakId)
        const kandidater = this.grupperINyeOgOppdaterte(filer, eksisterendeDokument);

        const oppdaterteDokumenter = from(kandidater.oppdaterte)
          .pipe(concatMap(fil => this._registrerNyDokumentVersjon(sakId, fil, filer.aksessToken)));

        const nyeDokumenter: Observable<OpplastetDokument<SKYFIL>> = from(kandidater.nye)
          .pipe(concatMap(fil => this._registrerNyttDokument(sakId, fil, filer.aksessToken)));

        return concat(oppdaterteDokumenter, nyeDokumenter);
    }

    grupperINyeOgOppdaterte(filer: ValgteSkyfiler<SKYFIL>, eksisterendeDokument: EksisterendeDokumentFunksjon): FilerGruppert<SKYFIL> {
        const filerMappet = filer.dokumenter
          .map(fil => ({
              fil: fil,
              eksisterende: eksisterendeDokument(fil.filnavn) ?? null
          }));

        return {
            nye: filerMappet.filter(f => !f.eksisterende),
            oppdaterte: filerMappet.filter(f => f.eksisterende)
        };
    }

    // Implementeres av subklasse

    protected abstract somSpesifikasjon(fil: DokumentOgEksisterende<SKYFIL>, aksessToken: string): SPESIFIKASJON ;

    protected abstract registrerNyttDokument(sakId: AgendaelementId, spesifikasjon: SPESIFIKASJON): Promise<SaksdokumentModell>;

    protected abstract registrerNyDokumentVersjon(sakId: AgendaelementId, dokumentId: DokumentId, spesifikasjon: SPESIFIKASJON): Promise<SaksdokumentModell>;

    // private hjelpemetoder

    private _registrerNyttDokument(sakId: AgendaelementId, fil: DokumentOgEksisterende<SKYFIL>, aksessToken: string): Promise<OpplastetDokument<SKYFIL>> {
        this.debug('registrerNyeDokumenter', fil)
        return this.registrer(fil, aksessToken, spesifikasjon => this.registrerNyttDokument(sakId, spesifikasjon))
    }

    private _registrerNyDokumentVersjon(sakId: AgendaelementId, fil: DokumentOgEksisterende<SKYFIL>, aksessToken: string): Promise<OpplastetDokument<SKYFIL>> {
        this.debug('registrerNyeVersjoner', fil)
        return this.registrer(fil, aksessToken, spesifikasjon => this.registrerNyDokumentVersjon(sakId, fil.eksisterende!.dokumentId, spesifikasjon))
    }

    private async registrer(fil: DokumentOgEksisterende<SKYFIL>, aksessToken: string, lastOpp: (spesifikasjon: SPESIFIKASJON) => Promise<SaksdokumentModell>): Promise<OpplastetDokument<SKYFIL>> {
        const spesifikasjon = this.somSpesifikasjon(fil, aksessToken);
        try {
            const opprettetDokument = await lastOpp(spesifikasjon);
            return this.somOpplastetDokument(fil, opprettetDokument);
        } catch (feil) {
            return this.somOpplastetDokumentFeilet(fil, feil);
        }
    }

    private somOpplastetDokument(fil: DokumentOgEksisterende<SKYFIL>, nyttEllerOppdatertDokument: SaksdokumentModell): OpplastetDokument<SKYFIL> {
        return {opplastetFil: fil.fil, nyttEllerOppdatertDokument: nyttEllerOppdatertDokument};
    }

    private somOpplastetDokumentFeilet(fil: DokumentOgEksisterende<SKYFIL>, feil: any): OpplastetDokument<SKYFIL> {
        return {opplastetFil: fil.fil, nyttEllerOppdatertDokument: null, feil: feil};
    }
}

export type EksisterendeDokumentFunksjon = (filnavn: string) => SaksdokumentModell | null;


export interface ValgteSkyfiler<SKYFIL extends Skyfil> {
    aksessToken: string
    dokumenter: SKYFIL[];
}

export interface FilerGruppert<SKYFIL extends Skyfil> {
    oppdaterte: DokumentOgEksisterende<SKYFIL>[];
    nye: DokumentOgEksisterende<SKYFIL>[]
}

export interface DokumentOgEksisterende<SKYFIL extends Skyfil> {
    /** skylfila valgt av bruker */
    fil: SKYFIL;
    /** eventuelt eksisterende dokument i lederplan med samme navn */
    eksisterende: SaksdokumentModell | null;
}

export interface OpplastetDokument<SKYFIL extends Skyfil> {
    opplastetFil: SKYFIL;
    /** SaksdokumentModell dersom dokumentet ble lastet opp uten feil, null dersom feil oppstod */
    nyttEllerOppdatertDokument: SaksdokumentModell | null;
    /** Satt til eventuell feil som oppstod ved opplasting */
    feil?: any;
}

export interface Skyfil extends ValgtFil {

}
